import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Video, Sparkles, Send, ShieldCheck, ArrowRight, Wrench, Play, Mic, Zap, TrendingUp, ClipboardCheck, Eye, BarChart3, AlertCircle, FileX, MessageSquareOff, Clock, TimerOff, Download, Rocket, Loader2, Database, Globe2, Lock, Calculator, Mail, BadgeCheck, Building2 } from "lucide-react";
import PitchDeckRequestModal from "@/components/PitchDeckRequestModal";
import { BrandLink } from "@/components/Brand";
import { SplashHero } from "@/components/SplashHero";
import { useAuth } from "@/context/AuthContext";
import { BRAND_PROFILES } from "@/lib/brandProfile";
import { usePublicTheme } from "@/lib/usePublicTheme";
import { toast } from "sonner";

// =========================================================================
// LOGO STRIP — "Currently in conversation with"
// -------------------------------------------------------------------------
// HIDDEN BY DEFAULT. Flip ENABLED to `true` only when you have permission
// from every group listed. Even a single logo turns the page from "concept"
// to "live deal flow" in an investor's eye.
//
// To enable:
//   1. Set ENABLED = true
//   2. Edit LOGOS below — `name` is the dealer/group name (always required),
//      `src` is an optional image path under /public/logos/.
//      If `src` is omitted, the name renders as a clean wordmark.
// =========================================================================
const LOGO_STRIP = {
  ENABLED: false,
  LABEL: "Currently in conversation with",
  LOGOS: [
    // { name: "RRG Group" },
    // { name: "Toyota GB" },
    // { name: "Drive Vauxhall" },
    // { name: "AutoTrader", src: "/logos/autotrader.svg" },
  ],
};

export default function LandingPage() {
  usePublicTheme();
  const detailsRef = useRef(null);
  const scrollDown = () => detailsRef.current?.scrollIntoView({ behavior: "smooth" });
  const { launchToyotaDemo } = useAuth();
  const nav = useNavigate();
  // Public marketing pages always render the default ServiceSell PRO brand,
  // regardless of any active brand profile. The white-label switch is
  // intentionally gated to the post-login experience so the prospect sees
  // the canonical product first, then a "we can rebrand this for you" reveal.
  const brand = BRAND_PROFILES.default;
  const [toyotaBusy, setToyotaBusy] = useState(false);
  const [deckOpen, setDeckOpen] = useState(false);

  const launchToyota = async () => {
    setToyotaBusy(true);
    try {
      const { credentials } = await launchToyotaDemo();
      toast.success(`Demo workshop ready · login: ${credentials.email}`, { duration: 8000 });
      nav("/dashboard");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't spin up the demo — please try again";
      toast.error(msg);
    } finally {
      setToyotaBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Splash */}
      <SplashHero onScrollDown={scrollDown} />

      <div ref={detailsRef} />

      {/* Nav (becomes visible after splash) */}
      <header className="border-b border-border bg-card/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <BrandLink />
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" className="btn-outline whitespace-nowrap" data-testid="nav-login-btn">Sign in</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 text-foreground text-xs font-bold uppercase tracking-widest mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" /> Enterprise aftersales platform · UK GDPR · Hosted in the UK
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-foreground">
              Inspect.<br />
              <span className="text-primary">Advise.</span> Sell.
            </h1>
            <p className="mt-6 text-base md:text-lg text-foreground/80 max-w-xl leading-relaxed">
              Technicians film what they see. {brand.productName} adds parts &amp; labour, generates a customer-friendly explanation,
              and sends a one-tap approval link. Customers say yes — faster.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button type="button" onClick={launchToyota} disabled={toyotaBusy} className="btn-primary text-base" data-testid="hero-launch-toyota-demo-btn">
                {toyotaBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                {toyotaBusy ? "Staging boardroom demo…" : "Launch boardroom demo"}
              </button>
              <Link to="/roi-calculator" className="btn-outline text-base" data-testid="hero-cta-roi-btn">
                <Calculator className="w-4 h-4" /> See your ROI
              </Link>
              <button type="button" onClick={() => setDeckOpen(true)} className="btn-outline text-base" data-testid="hero-pitch-deck-btn">
                <Mail className="w-4 h-4" /> Email me the pitch deck
              </button>
            </div>
            <div className="mt-3 text-[11px] text-muted-foreground/80 max-w-xl" data-testid="launch-demo-hint">
              <span className="font-semibold text-foreground">Boardroom demo:</span> a fully-staged tenant with six live-stage jobs — booked, checked-in, sent, partial approval, completed and tyre-tier chooser. Ready in seconds, no setup required.
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-success" /> Customer-authorised work</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> AI explanations built-in</div>
            </div>
            <div className="mt-4 text-xs text-muted-foreground/70" data-testid="hero-ai-credibility">
              Built on UK infrastructure · Audited · Encrypted end-to-end
            </div>

            {/* Credentials strip — a dealer principal scans for these before he
                trusts a one-man software supplier with his customer data. Real
                registration numbers, no badges we haven't earned. */}
            <div
              className="mt-5 flex flex-wrap items-center gap-2"
              data-testid="hero-credentials-strip"
            >
              {[
                { icon: BadgeCheck, label: "Registered UK trade mark", value: "UK00004398263", testid: "credential-trademark" },
                { icon: Database, label: "ICO registered", value: "ZC144538", testid: "credential-ico" },
                { icon: Building2, label: "Registered in England & Wales", value: "No. 17211707", testid: "credential-company" },
                { icon: Globe2, label: "UK hosted", value: "Data stays in the UK", testid: "credential-hosting" },
              ].map(({ icon: Icon, label, value, testid }) => (
                <div
                  key={testid}
                  data-testid={testid}
                  className="group inline-flex items-center gap-2 rounded-full border border-border/80 bg-card/50 pl-2.5 pr-3 py-1.5 transition-colors duration-200 hover:border-success/50 hover:bg-card"
                >
                  <Icon className="w-3.5 h-3.5 text-success shrink-0 transition-transform duration-200 group-hover:scale-110" />
                  <span className="text-[11px] leading-tight">
                    <span className="font-semibold text-foreground">{label}</span>
                    <span className="text-muted-foreground/80"> · {value}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="relative card-default p-1 shadow-2xl rotate-1 hover:rotate-0 transition-all duration-500">
              <div className="aspect-[4/5] rounded-md overflow-hidden relative">
                <img src="https://images.unsplash.com/photo-1487754180451-c456f719a1fc?auto=format&fit=crop&w=1200&q=80" alt="Modern workshop" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="text-xs font-bold uppercase tracking-widest opacity-80">Quote #A-2310</div>
                  <div className="font-display font-bold text-2xl mt-1">Brake pads + 2 more</div>
                  <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider">
                    <Video className="w-3 h-3" /> Video attached
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -left-4 -bottom-4 card-default p-4 max-w-[200px] -rotate-2 hidden md:block">
              <div className="label-overline">Customer says</div>
              <div className="text-sm font-semibold mt-1">"Approved — book me in for Tuesday."</div>
            </div>
          </div>
        </div>
      </section>

      {/* Logo strip — dormant until LOGO_STRIP.ENABLED is flipped to true.
          Sits between the hero and the "see more" CTA so it reads as
          immediate social proof. */}
      {LOGO_STRIP.ENABLED && LOGO_STRIP.LOGOS.length > 0 && (
        <section className="border-b border-border bg-card/30" data-testid="logo-strip-section">
          <div className="max-w-7xl mx-auto px-6 py-10 md:py-12">
            <div className="text-center text-[11px] font-mono uppercase tracking-[0.3em] text-muted-foreground mb-6">
              {LOGO_STRIP.LABEL}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 opacity-80">
              {LOGO_STRIP.LOGOS.map((l) => (
                <div key={l.name} className="flex items-center justify-center h-10" data-testid={`logo-${l.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  {l.src ? (
                    <img src={l.src} alt={l.name} className="max-h-10 w-auto grayscale hover:grayscale-0 transition-all" />
                  ) : (
                    <span className="font-display font-black tracking-tight text-xl md:text-2xl text-foreground/80 hover:text-foreground transition-colors">
                      {l.name}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightweight CTA — visitors choose how deep they want to go */}
      <section className="border-b border-border bg-zinc-950" data-testid="see-more-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14 text-center">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary/80 mb-3">Like what you see?</div>
          <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-white">
            Want to see <span className="text-primary">more?</span>
          </h2>
          <p className="mt-3 text-white/65 text-sm md:text-base">
            Watch the 60-second cinematic — or jump straight in and try a live demo workshop.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <Link
              to="/watch"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary text-primary-foreground font-bold text-sm hover:scale-[1.02] transition-transform"
              data-testid="see-more-watch-btn"
            >
              <Play className="w-4 h-4" fill="currentColor" /> Watch the 60-sec demo
            </Link>
          </div>
        </div>
      </section>

      {/* Problem statement */}
      <section className="border-b border-border bg-zinc-950 text-white" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-destructive/15 border border-destructive/30 text-destructive text-[11px] font-bold uppercase tracking-[0.25em] mb-5">
                <AlertCircle className="w-3.5 h-3.5" /> Problem statement
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[0.95]">
                Modern dealerships lose <span className="text-destructive">significant aftersales revenue</span>.
              </h2>
              <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-md">
                Current workshop video systems often stop at the inspection stage and still rely heavily on manual advisor intervention.
              </p>
              <div className="mt-8 hidden md:block">
                <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/40 mb-3">The result</div>
                <ul className="space-y-2.5">
                  {[
                    { icon: TimerOff,            t: "Slow turnaround times" },
                    { icon: FileX,               t: "Inconsistent quoting" },
                    { icon: TrendingUp,          t: "Reduced conversion rates" },
                    { icon: MessageSquareOff,    t: "Poor customer transparency" },
                  ].map((it, i) => (
                    <li key={it.t} className="flex items-center gap-3 text-white/85" data-testid={`problem-result-${i}`}>
                      <span className="grid place-items-center w-7 h-7 rounded-md bg-destructive/15 border border-destructive/30 shrink-0">
                        <it.icon className="w-3.5 h-3.5 text-destructive" />
                      </span>
                      <span className="text-sm font-semibold">{it.t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="md:col-span-7">
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/40 mb-4">Why dealerships lose revenue</div>
              <ul className="space-y-3">
                {[
                  { t: "Technician findings are manually processed",      d: "Hand-written sheets, voice notes and second-trip explanations all introduce delay and error." },
                  { t: "Advisors spend excessive time building quotes",   d: "Looking up parts, calculating labour and re-typing the same explanations across every job." },
                  { t: "Customer communication is delayed",               d: "Phone tag, voicemail, callbacks — the customer is the slowest part of the entire process." },
                  { t: "Approvals arrive too late or not at all",         d: "Decisions get postponed past the booking, and amber/red items roll forward indefinitely." },
                  { t: "Upsell opportunities are missed",                 d: "Without a clear, calm explanation, customers default to 'I'll think about it' — and never come back." },
                ].map((p, i) => (
                  <li
                    key={p.t}
                    className="group flex items-start gap-4 rounded-xl border border-white/10 hover:border-destructive/40 bg-white/[0.02] hover:bg-white/[0.04] p-5 transition-all"
                    data-testid={`problem-point-${i}`}
                  >
                    <div className="shrink-0 w-9 h-9 rounded-lg bg-destructive/10 border border-destructive/30 grid place-items-center font-mono-plex text-destructive font-bold text-sm">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <div className="font-display font-bold text-base md:text-lg leading-tight">{p.t}</div>
                      <p className="mt-1 text-sm text-white/60 leading-relaxed">{p.d}</p>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Mobile-only "result" repeat — keeps it visible on small screens */}
              <div className="mt-8 md:hidden">
                <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/40 mb-3">The result</div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { icon: TimerOff,         t: "Slow turnaround" },
                    { icon: FileX,            t: "Inconsistent quoting" },
                    { icon: TrendingUp,       t: "Lower conversion" },
                    { icon: MessageSquareOff, t: "Poor transparency" },
                  ].map((it) => (
                    <div key={it.t} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-destructive/30 bg-destructive/10">
                      <it.icon className="w-3.5 h-3.5 text-destructive" />
                      <span className="text-xs font-semibold">{it.t}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bridge into the solution */}
              <div className="mt-8 inline-flex items-center gap-2 text-sm text-white/60">
                <ArrowRight className="w-4 h-4 text-primary" />
                <span>Here's what changes when {brand.productName}{brand.key === "default" ? <>&trade;</> : null} runs the floor →</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What dealerships gain — front-page benefits strip */}
      <section className="border-b border-border bg-black text-white" data-testid="dealerships-gain-section">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-primary mb-4">For dealer principals</div>
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[0.95]">
                What dealerships <span className="text-primary">gain</span>.
              </h2>
              <p className="mt-5 text-white/70 text-base md:text-lg leading-relaxed max-w-md">
                Four outcomes you can measure from week one — without changing your DMS or retraining your team.
              </p>
            </div>
            <ul className="md:col-span-8 grid sm:grid-cols-2 gap-4">
              {[
                { icon: Zap,            t: "Increased workshop efficiency",     d: "Pre-calls, automated check-in, AI-drafted quotes and one-tap approvals strip wasted minutes from every job — your bays turn over faster." },
                { icon: TrendingUp,     t: "Increased approved work",           d: "Plain-English video explanations and traffic-light grading lift amber/red item conversion by double digits, week after week." },
                { icon: ClipboardCheck, t: "Reduced advisor admin time",        d: "No typing up phone calls. AI write-ups, message templates and one-tap follow-ups give advisors hours back each day." },
                { icon: Eye,            t: "Improved customer transparency",    d: "Customers see exactly what the technician sees, in real time — trust climbs, complaints drop, retention rises." },
              ].map((item, i) => (
                <li
                  key={item.t}
                  className="group relative rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-primary/40 p-5 transition-all duration-300"
                  data-testid={`dealerships-gain-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <span className="shrink-0 w-10 h-10 rounded-lg bg-primary/15 border border-primary/30 grid place-items-center group-hover:bg-primary/25 transition-colors">
                      <item.icon className="w-5 h-5 text-primary" strokeWidth={2} />
                    </span>
                    <div>
                      <div className="font-display font-bold text-lg leading-tight">{item.t}</div>
                      <p className="mt-1.5 text-sm text-white/65 leading-relaxed">{item.d}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="label-overline mb-3">How it works</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight max-w-2xl">
          Four steps from inspection to <span className="text-primary">authorised work</span>.
        </h2>
        <div className="grid md:grid-cols-4 gap-4 mt-12">
          {[
            { n: "01", icon: Video, t: "Record", d: "Whilst inspecting the vehicle on the ramp, the technician records a thorough walkaround — showing healthy components and those needing attention. Any length." },
            { n: "02", icon: Wrench, t: "Grade", d: "Each item is colour-coded: Green (healthy), Amber (advisory), Red (urgent). Pricing auto-fills from your catalog." },
            { n: "03", icon: Sparkles, t: "Explain", d: "AI writes a calm, plain-English breakdown of the report for your customer." },
            { n: "04", icon: Send, t: "Send", d: "One link. Customer approves or declines each amber & red item from their phone." },
          ].map((s, i) => (
            <div key={s.n} className="card-default p-6 hover:-translate-y-1 transition-all duration-300" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="flex items-start justify-between">
                <div className="font-mono-plex text-xs font-bold text-primary">{s.n}</div>
                <s.icon className="w-5 h-5 text-foreground/60" strokeWidth={2} />
              </div>
              <h3 className="font-display font-bold text-xl mt-6">{s.t}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Traffic-light system */}
      <section className="border-t border-border bg-card/30">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-5">
              <div className="label-overline mb-3">The traffic-light system</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                The same grading customers <span className="text-primary">trust at MOT.</span>
              </h2>
              <p className="mt-5 text-foreground/80 leading-relaxed">
                Every item in the inspection is graded with a colour. Customers instantly know what's healthy,
                what to plan for, and what needs doing now — no jargon, no sales pressure.
              </p>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-3 gap-4">
              {[
                {
                  light: "bg-success",
                  pill: "bg-success/15 text-success border-success/30",
                  title: "Good",
                  sub: "Healthy",
                  body: "Inspected and within tolerance. No action required — the customer leaves reassured.",
                },
                {
                  light: "bg-warning",
                  pill: "bg-warning/15 text-warning border-warning/30",
                  title: "Advisory",
                  sub: "Watch / plan",
                  body: "Work to consider in the next 6-12 months, or before the next service or MOT.",
                },
                {
                  light: "bg-destructive",
                  pill: "bg-destructive/15 text-destructive border-destructive/30",
                  title: "Requires attention",
                  sub: "Urgent / safety",
                  body: "Recommended immediately — usually safety-related or to avoid a much bigger repair.",
                },
              ].map((t, i) => (
                <div key={i} className="card-default p-5 hover:-translate-y-1 transition-all">
                  <div className="flex items-center justify-between">
                    <div className={`w-3 h-3 rounded-full ${t.light}`} />
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${t.pill}`}>{t.sub}</span>
                  </div>
                  <div className="font-display font-bold text-2xl mt-4 tracking-tight">{t.title}</div>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{t.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes — measured, no fictional names */}
      <section className="border-t border-border bg-card/30">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="label-overline mb-3">Measured outcomes</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight max-w-3xl">
            What workshops change <span className="text-primary">when the floor runs on video</span>.
          </h2>
          <p className="mt-4 text-foreground/70 max-w-2xl text-base leading-relaxed">
            Benchmark ranges observed when workshops switch from phone-call advisories to video-led, AI-explained quotes.
            Your numbers will vary by mix, brand and bay count — the ROI calculator models it against your own data.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12" data-testid="outcomes-stats">
            {[
              { v: "+38%", label: "Amber/red close rate uplift",       sub: "vs. phone-only follow-up" },
              { v: "20 min", label: "Advisor time saved per job",       sub: "drafting & explaining quotes" },
              { v: "52%",  label: "Of declines reconsider",              sub: "after safety follow-up" },
              { v: "<24h", label: "Average approval turnaround",         sub: "down from 2-3 days" },
            ].map((s) => (
              <div key={s.label} className="card-default p-6 hover:-translate-y-1 transition-all">
                <div className="font-display font-black text-4xl md:text-5xl tracking-tighter text-primary">{s.v}</div>
                <div className="mt-3 font-semibold text-sm text-foreground leading-tight">{s.label}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Positioning band — the "system of record vs system of engagement"
          line lifted verbatim from the pitch cheat sheet. Sits between
          numbers and the safety-follow-up strip so it lands as the
          intellectual punchline after the proof. */}
      <section className="border-t border-border bg-zinc-950 text-white" data-testid="positioning-band">
        <div className="max-w-5xl mx-auto px-6 py-20 md:py-28 text-center">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary/80 mb-6">
            How we sit alongside your stack
          </div>
          <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl md:text-5xl leading-[1.1] text-balance">
            <span className="block">Your DMS is the <span className="text-white/55">system of record</span>.</span>
            <span className="block mt-2 sm:mt-3">{brand.productName} is the <span className="text-primary">system of engagement</span> on top.</span>
          </h2>
          <p className="mt-8 text-white/60 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Same architecture as Salesforce on SAP. Your DMS holds the truth — we capture the eyes,
            the videos and the approvals. They win together.
          </p>
        </div>
      </section>

      {/* Re-engagement strip */}
      <section className="bg-black text-white border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="label-overline text-white/60 mb-3">When customers say no</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              We change minds, <span className="text-primary">safely</span>.
            </h2>
            <p className="mt-4 text-white/70 leading-relaxed max-w-lg">
              When a customer declines critical work — like brake pads — {brand.productName} sends a short, caring follow-up
              video and a written safety brief. No pressure. Just clarity.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="aspect-square rounded-lg overflow-hidden">
              <img src="https://images.unsplash.com/photo-1760317890322-364a810cd4da?auto=format&fit=crop&w=900&q=80" alt="Brakes" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square rounded-lg overflow-hidden mt-8">
              <img src="https://images.unsplash.com/photo-1691706268537-27b719ee00dd?auto=format&fit=crop&w=900&q=80" alt="Wheel alignment" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-10 space-y-6 text-sm text-muted-foreground">
          {/* Trust seals — directors look for these before sending us procurement data.
              GDPR, ICO, UK hosting, encryption — each one quells a specific objection. */}
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
            data-testid="footer-trust-seals"
          >
            <div className="flex items-start gap-3 rounded-md border border-border bg-card/40 p-4">
              <ShieldCheck className="w-5 h-5 text-success mt-0.5 shrink-0" />
              <div>
                <div className="font-bold text-xs uppercase tracking-wider text-foreground">UK GDPR compliant</div>
                <div className="text-[11px] mt-0.5">Data processed lawfully under UK GDPR &amp; the Data Protection Act 2018.</div>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-md border border-border bg-card/40 p-4">
              <Database className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <div className="font-bold text-xs uppercase tracking-wider text-foreground">ICO registered</div>
                <div className="text-[11px] mt-0.5">Information Commissioner&apos;s Office registration ZC144538.</div>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-md border border-border bg-card/40 p-4">
              <Globe2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <div className="font-bold text-xs uppercase tracking-wider text-foreground">Hosted in the UK</div>
                <div className="text-[11px] mt-0.5">All customer data resides on UK infrastructure — never leaves the country.</div>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-md border border-border bg-card/40 p-4">
              <Lock className="w-5 h-5 text-success mt-0.5 shrink-0" />
              <div>
                <div className="font-bold text-xs uppercase tracking-wider text-foreground">Encrypted &amp; audit-logged</div>
                <div className="text-[11px] mt-0.5">TLS 1.3 in transit · AES-256 at rest · every change timestamped.</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2 border-t border-border/60">
            <div>© {new Date().getFullYear()} {brand.dealerName} · Inspect · Advise · Sell · Grow.</div>
            <div className="flex items-center gap-4">
              <button type="button" onClick={() => setDeckOpen(true)} className="hover:text-foreground" data-testid="footer-pitch-deck-link">Pitch deck</button>
              <Link to="/roi-calculator" className="hover:text-foreground" data-testid="footer-roi-link">ROI calculator</Link>
              <Link to="/pilot" className="hover:text-foreground" data-testid="footer-pilot-link">30-day pilot</Link>
              <Link to="/privacy" className="hover:text-foreground" data-testid="footer-privacy-link">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground" data-testid="footer-terms-link">Terms</Link>
            </div>
          </div>
          <div className="text-xs text-muted-foreground/80 leading-relaxed" data-testid="footer-company-details">
            ServiceSellPro Limited · Registered in England &amp; Wales · Company No. 17211707 · ICO ZC144538 · Registered office: 25 Meadow Park, Kirkheaton, Huddersfield HD5 0HX
          </div>
          <div className="text-xs text-muted-foreground/80 leading-relaxed mt-2" data-testid="footer-trademark">
            <strong>ServiceSell PRO&reg;</strong> is a registered UK trade mark. Registration No. UK00004398263 · Classes 9 (software) &amp; 42 (SaaS / hosted services) · Registered at the UK Intellectual Property Office, 28 August 2026.
          </div>
        </div>
      </footer>

      <PitchDeckRequestModal open={deckOpen} onClose={() => setDeckOpen(false)} source="landing" />
    </div>
  );
}
